import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useCharitableGivingGoalsQuery } from './api/useCharitableGivingGoalsQuery';
import { useResetModuleWrapperStyles } from '../_hooks/useResetModuleWrapperStyles';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Content, StickyFooter } from '../CommonComponents/OPL/StickyFooter/StickyFooter';
import { FullPageLoading } from '../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { HtmlTable } from '../CommonComponents/OPL/HtmlTable/HtmlTable';
import { IconButton } from '../CommonComponents/OPL/IconButton/IconButton';
import { ScrollHereOnce } from '../CommonComponents/ScrollHereOnce';
import { DeleteGoalModal } from './components/DeleteGoalModal/DeleteGoalModal';
import { TruncatedText } from '../CommonComponents/OPL/Text/TruncatedText';
import { selectOnboardingWorkflow } from '../CommonSelectors/CommonAreaSelector';
import { OnboardingWorkflow } from '../CommonSelectors/CommonArea.types';

interface Props {
  clientId: string;
}

export const CharitableGivingWorkflowGoals = ({ clientId }: Props) => {
  useResetModuleWrapperStyles();

  useDocumentTitle('Charitable Giving - Workflow All Goals');

  const { isLoading, isError, refetch, data } = useCharitableGivingGoalsQuery();

  const [goalIdToDelete, setGoalIdToDelete] = useState<string | null>(null);

  const onboardingWorkflow = useSelector(selectOnboardingWorkflow);

  if (isLoading) {
    return (
      <Box mt={96}>
        <Content>
          <FullPageLoading text="Loading..." size="medium" />
        </Content>

        <StickyFooter />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box mt={96}>
        <Content display="grid" css="place-items: center">
          <ErrorState onAction={refetch} />
        </Content>

        <StickyFooter />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const onAddNewGoal = () => {
    ADV.util.Router.go2('CharitableGivingWorkflow', {
      clientId,
    });
  };

  const onEditGoal = (goalId: string) => {
    ADV.util.Router.go2('CharitableGivingWorkflow', {
      clientId,
      goalId,
    });
  };

  const AddGoalButton = (
    <Button leftIcon="fas fa-plus" variant="link" onClick={onAddNewGoal}>
      Add a New Charitable Giving Goal
    </Button>
  );

  return (
    <>
      <ScrollHereOnce options={false} />

      <Content>
        <Box maxWidth="712px" mt={96} mx="auto">
          <HtmlTable>
            <table>
              <colgroup>
                <col width="40%" />
                <col width="30%" />
                <col width="30%" />
                <col width="0%" />
              </colgroup>

              <caption>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Text as="h1" fontSize="h6" fontWeight={600}>
                    Manage Your Charitable Giving Goals
                  </Text>

                  {data.length > 0 && AddGoalButton}
                </Box>
              </caption>

              {data.length > 0 && (
                <thead>
                  <tr>
                    <th>
                      <Text variant="tableHeader" textAlign="left">
                        Name
                      </Text>
                    </th>

                    <th>
                      <Text variant="tableHeader" textAlign="left">
                        Donation Start Year
                      </Text>
                    </th>

                    <th>
                      <Text variant="tableHeader" textAlign="left">
                        Donation End Year
                      </Text>
                    </th>

                    <th aria-label="Action Items" />
                  </tr>
                </thead>
              )}

              {data.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={4} style={{ paddingTop: 16, paddingBottom: 16 }}>
                      {AddGoalButton}
                    </td>
                  </tr>
                </tbody>
              )}

              {data.length > 0 && (
                <tbody>
                  {data.map(goal => (
                    <tr key={goal.id}>
                      <td>
                        <TruncatedText fontWeight={500} maxWidth={150}>
                          {goal.name}
                        </TruncatedText>
                      </td>

                      <td>
                        <TruncatedText fontWeight={500} maxWidth={150}>
                          {goal.donationStartYear}
                        </TruncatedText>
                      </td>

                      <td>
                        <TruncatedText fontWeight={500} maxWidth={150}>
                          {goal.donationEndYear}
                        </TruncatedText>
                      </td>

                      <td>
                        <Box display="flex" style={{ gap: 2 }}>
                          <IconButton
                            icon="far fa-pencil"
                            variant="subtleInactive"
                            onClick={() => onEditGoal(goal.id)}
                            aria-label="Edit goal"
                          />

                          <IconButton
                            icon="far fa-minus-circle"
                            variant="subtleInactive"
                            onClick={() => setGoalIdToDelete(goal.id)}
                            aria-label="Delete goal"
                          />
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </HtmlTable>
        </Box>
      </Content>

      <StickyFooter progress={100} progressVariant="success">
        {onboardingWorkflow !== OnboardingWorkflow.CharitableGivingWorkflow && (
          <Button
            variant="secondary"
            size="large"
            onClick={() => ADV.util.Router.go2('ClientEditPlan', { clientId })}
            leftIcon="fas fa-chevron-left"
            mr="auto"
          >
            Back
          </Button>
        )}
      </StickyFooter>

      {goalIdToDelete && (
        <DeleteGoalModal goalId={goalIdToDelete} onClose={() => setGoalIdToDelete(null)} />
      )}
    </>
  );
};
